<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '../../../components/Button.svelte';
  import Stepper from '../../../components/Stepper.svelte';
  import StageBadge from '../../../components/StageBadge.svelte';
  import { stageFromStatusName, type LifecycleStage } from '../../../lib/donationLifecycle';
  import BackButton from '../../../components/BackButton.svelte';
  import toast from 'svelte-french-toast';
  import { type DonationRequest } from '../../../lib/api/employer/leaverequest';
  import employerLeaveRequestService from '../../../lib/api/employer/leaverequest';
  import RejectRequestModal from './RejectRequestModal.svelte';
  import { push } from 'svelte-spa-router';
  import { refreshEmployerPendingActions } from '../../../stores/employer-pending-actions.store';


  // Get request ID from prop or URL parameter
  export let requestId: string = '';
  
  let loading = true;
  let request: DonationRequest | null = null;
  let error: string | null = null;
  let fallbackLogoSrc = '/placeholder-logo.png';
  $: charityLogoSrc = request?.charity?.logo || fallbackLogoSrc;
  let loadingTimeout: ReturnType<typeof setTimeout>;
  let showRejectModal = false;
  let rejectReason = '';
  
  const fetchRequestDetails = async (id: string) => {
    if (!id) return null;
    
    try {
      const result = await employerLeaveRequestService.getLeaveRequestDetail(id);
      return result;
    } catch (err) {
      console.error('Error fetching request details:', err);
      throw err;
    }
  };

  function handleImageError(e: Event) {
    const imgElement = e.target as HTMLImageElement;
    imgElement.src = fallbackLogoSrc;
  }

  // Set a minimum loading time to prevent flashing
  const setMinimumLoadingTime = () => {
    return new Promise(resolve => {
      loadingTimeout = setTimeout(() => {
        resolve(true);
      }, 300);
    });
  };

  // Get the actual request ID from prop or URL
  function getRequestId(): string {
    if (requestId) return requestId;
    // For hash-based routing, query params are in the hash, not search
    const hash = window.location.hash;
    const queryString = hash.includes('?') ? hash.split('?')[1] : '';
    if (queryString) {
      const params = new URLSearchParams(queryString);
      return params.get('id') || '';
    }
    return '';
  }

  // Load data from API
  const loadData = async () => {
    const id = getRequestId();
    
    if (!id) {
      error = 'Request ID is required';
      loading = false;
      toast.error('Request ID is required');
      return;
    }
    
    const loadingPromise = setMinimumLoadingTime();
    
    try {
      // Fetch data in parallel with minimum loading time
      const [result] = await Promise.all([
        fetchRequestDetails(id),
        loadingPromise
      ]);
      
      if (result) {
        request = result;
      } else {
        error = 'Request not found';
        toast.error('Request not found');
      }
    } catch (err: any) {
      error = 'Failed to load request details';
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load request details';
      toast.error(errorMessage);
      console.error('Error loading request:', err);
    } finally {
      loading = false;
    }
  };

  // Initialize on mount
  onMount(() => {
    // Always read directly from URL to avoid timing issues
    const id = getRequestId();
    
    if (id) {
      // Update requestId if we got it from URL
      if (!requestId && id) {
        requestId = id;
      }
      loadData();
    } else {
      error = 'Request ID is required';
      loading = false;
      toast.error('Request ID is required');
    }
    
    // Return cleanup function
    return () => {
      if (loadingTimeout) clearTimeout(loadingTimeout);
    };
  });

  async function handleApprove() {
    // Navigate to update page where approval with payment details happens
    const id = getRequestId();
    if (id) {
      push(`/update-leave-request?id=${id}`);
    }
  }

  async function handleReject() {
    if (!request) return;
    
    loading = true;
    try {
      await employerLeaveRequestService.rejectLeaveRequest(request.uuid, rejectReason);
      // Refresh the request data
      const refreshId = getRequestId();
      if (refreshId) {
        const result = await fetchRequestDetails(refreshId);
        request = result;
        await refreshEmployerPendingActions();
        toast.success('Request rejected successfully');
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to reject request';
      toast.error(errorMsg);
    } finally {
      loading = false;
    }
  }

  function handleOpenRejectModal() {
    showRejectModal = true;
  }
  
  function handleCloseRejectModal() {
    showRejectModal = false;
  }
  
  function handleSubmitRejection() {
    handleReject();
    showRejectModal = false;
  }

  // Canonical lifecycle stage (prefer backend value; fall back to status name).
  $: stage = ((request?.lifecycle_stage as LifecycleStage) || stageFromStatusName(request?.donation_request_status?.name));

  // Check if request is pending and can be acted upon
  $: isPending = request?.donation_request_status?.name?.toLowerCase() === 'pending';

  // Rejected requests have no authorisation document, so the download action is meaningless.
  $: isRejected = request?.donation_request_status?.name?.toLowerCase() === 'rejected';

  $: canDownloadPdf = !!(request?.has_authorisation_pdf) && !isRejected;

  let downloading = false;

  async function handleDownload() {
    if (!request?.uuid) return;
    downloading = true;
    try {
      await employerLeaveRequestService.downloadAuthorisationPdf(
        request.uuid,
        `Leave-Cashout-Authorisation-${request.uuid}.pdf`
      );
      toast.success('Download started');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      toast.error('This request does not have an authorisation PDF yet, or the download failed.');
    } finally {
      downloading = false;
    }
  }

  async function handleEmail() {
    if (!request) return;
    
    try {
      const subject = encodeURIComponent(`Leave Request Details - ${request.user?.full_name || 'Employee'}`);
      const body = encodeURIComponent(`
Leave Request Details

Employee: ${request.user?.full_name || 'N/A'}
Email: ${request.user?.email || 'N/A'}
Hours Requested: ${request.leave_hours}
Submission Date: ${new Date(request.created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
Status: ${request.donation_request_status?.name || 'Pending'}
Charity: ${request.charity?.name || 'N/A'}
${request.leave_value ? `Employee Contribution: $${Number(request.leave_value).toFixed(2)}` : ''}
${request.employer_top_up ? `Employer Matching: $${Number(request.employer_top_up).toFixed(2)}` : ''}
${request.employer_comment ? `Employer Notes: ${request.employer_comment}` : ''}
      `.trim());
      
      window.location.href = `mailto:${request.user?.email || ''}?subject=${subject}&body=${body}`;
    } catch (error) {
      console.error('Error opening email client:', error);
      toast.error('Failed to open email client');
    }
  }

</script>

<div class="view-request-page">
  <div class="back-button-container">
    <BackButton text="Back" onClick={() => window.history.back()} />
  </div>
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={() => location.reload()}>
        Try Again
      </Button>
    </div>
  {:else if request}
    <div class="request-content">
      <!-- Employee Information Section -->
      <section class="info-section employee-info-section">
        <h2 class="section-title">Employee Information</h2>
        <div class="employee-info-content">
          <div class="info-grid">
            <div class="info-row">
              <div class="info-field">
                <div class="field-container">
                  <span class="field-label">Employee Name</span>
                    {#if request.user?.uuid}
                    {@const employeeUuid = request.user.uuid}
                    <a href="/employees/{employeeUuid}" class="employee-link" on:click|preventDefault={() => push(`/employees/${employeeUuid}`)}>
                      <span class="field-value">{request.user?.full_name || 'Unknown'}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline; vertical-align: middle; margin-left: 4px;">
                        <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" fill="currentColor" />
                      </svg>
                    </a>
                  {:else}
                    <span class="field-value">{request.user?.full_name || 'Unknown'}</span>
                  {/if}
                </div>
              </div>
              <div class="info-field">
                <div class="field-container">
                  <span class="field-label">Email</span>
                  <span class="field-value">{request.user?.email || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Request Summary Section -->
      <section class="info-section">
        <div class="summary-header">
          <h2 class="section-title">Request Summary</h2>
          <div class="status-badge-container">
            <StageBadge {stage} />
          </div>
        </div>
        <div class="stepper-wrapper">
          <Stepper {stage} />
        </div>
        <div class="info-grid">
          <div class="info-row">
            <div class="info-field">
              <div class="field-container">
                <span class="field-label">Requested Hours</span>
                <span class="field-value">{request.leave_hours} Hours</span>
              </div>
            </div>
            <div class="info-field">
              <div class="field-container">
                <span class="field-label">Submission Date</span>
                <span class="field-value">{new Date(request.created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Financial Details & Notes Section -->
      {#if (request.donation_request_status?.name?.toLowerCase() === 'approved' || request.donation_request_status?.name?.toLowerCase() === 'processed') && request.leave_value}
        <section class="info-section">
          <h2 class="section-title">Donation Details</h2>
          <div class="info-grid">
            <div class="info-row">
              <div class="info-field">
                <div class="field-container">
                  <span class="field-label">Employee Contribution</span>
                  <span class="field-value amount-value">${Number(request.leave_value).toFixed(2)}</span>
                </div>
              </div>
              {#if request.employer_top_up && request.employer_top_up > 0}
                <div class="info-field">
                  <div class="field-container">
                    <span class="field-label">Employer Matching</span>
                    <span class="field-value amount-value">${Number(request.employer_top_up).toFixed(2)}</span>
                  </div>
                </div>
              {/if}
            </div>
            <div class="info-row">
              <div class="info-field">
                <div class="field-container total-container">
                  <span class="field-label">Total Donation</span>
                  <span class="field-value total-amount">
                    ${(Number(request.leave_value || 0) + Number(request.employer_top_up || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {#if request.employer_comment}
            <div class="note-container note-container-approved" style="margin-top: 16px;">
              <p class="employee-note"><strong>Employer Notes:</strong> {request.employer_comment}</p>
            </div>
          {/if}
        </section>
      {:else if request.donation_request_status?.name?.toLowerCase() === 'rejected' && request.rejection_reason}
        <!-- Rejection Notes Section (only show if rejected) -->
        <section class="info-section">
          <h2 class="section-title">Rejection Details</h2>
          <div class="note-container note-container-rejected">
            <p class="employee-note">{request.rejection_reason}</p>
          </div>
        </section>
      {/if}

      <!-- Charity Details Section -->
      <section class="info-section">
        <h2 class="section-title">Charity Details</h2>
        <div class="charity-container">
          <div class="charity-logo">
            <img 
              src={charityLogoSrc} 
              alt={request.charity?.name || 'Charity'}
              on:error={handleImageError}
            />
          </div>
          <div class="charity-info">
            {#if request.charity?.uuid}
              {@const charityUuid = request.charity.uuid}
              <a href="/employer/view-charity/{charityUuid}" class="charity-name-link" on:click|preventDefault={() => push(`/employer/view-charity/${charityUuid}`)}>
                <h3 class="charity-name">{request.charity?.name || 'N/A'}</h3>
              </a>
            {:else}
              <h3 class="charity-name">{request.charity?.name || 'N/A'}</h3>
            {/if}
            <p class="charity-reg">Reg. ID: {request.charity?.uuid || 'N/A'}</p>
          </div>
        </div>

        <div class="charity-details-container">
          <div class="charity-detail-section">
            <span class="detail-label">Cause</span>
            <div class="detail-value-container">
              <span class="detail-value-title">Environmental Conservation</span>
              <p class="charity-description">Supporting environmental conservation and sustainability initiatives across Australia.</p>
            </div>
          </div>
          
          <div class="charity-detail-section">
            <span class="detail-label">Location</span>
            <div class="detail-value-container">
              <span class="detail-value">{request.user?.location || "N/A"}</span>
            </div>
          </div>
        </div>
        
        {#if request.charity?.uuid}
          {@const charityUuid = request.charity.uuid}
          <a href="/employer/view-charity/{charityUuid}" class="charity-link" on:click|preventDefault={() => push(`/employer/view-charity/${charityUuid}`)}>
            View Full Charity Profile 
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline; vertical-align: middle; margin-left: 4px;">
              <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" fill="currentColor" />
            </svg>
          </a>
        {/if}
      </section>

      <!-- Action Buttons Section -->
      <section class="info-section">
        <h2 class="section-title">Actions</h2>
        <div class="action-buttons-group">
          {#if isPending}
            <div class="action-info">
              <p class="action-description">
                Ready to finalize this request?
              </p>
              <p class="action-detail">
                Approving will process the donation and notify the employee. Rejecting will require a reason.
              </p>
            </div>
            <div class="action-buttons">
              <Button variant="primary" on:click={handleApprove} disabled={loading}>
                Approve Request
              </Button>
              <Button variant="secondary" on:click={handleOpenRejectModal} disabled={loading}>
                Reject Request
              </Button>
            </div>
          {/if}
          <div class="utility-buttons">
            {#if !isRejected}
              <Button variant="secondary" on:click={handleDownload} disabled={loading || downloading || !canDownloadPdf}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Download
              </Button>
            {/if}
            <Button variant="secondary" on:click={handleEmail} disabled={loading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              Email
            </Button>
          </div>
        </div>
      </section>

    </div>
    <RejectRequestModal
      open={showRejectModal}
      bind:reason={rejectReason}
      onSubmit={handleSubmitRejection}
      onCancel={handleCloseRejectModal}
    />
  {/if}
</div>

<style>
  .view-request-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  .back-button-container {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 0;
    max-width: 600px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  .loading-container, .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin: 0 auto;
    max-width: 800px;
    width: 100%;
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .request-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0;
    max-width: 600px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  .info-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    padding: 24px;
  }

  .employee-info-section {
    background: white;
  }

  .employee-info-content {
    width: 100%;
  }

  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 24px 0;
  }

  .summary-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  .stepper-wrapper {
    margin: 8px 0 28px 0;
  }

  .status-badge-container {
    margin-right: 8px;
  }

  .info-grid {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    gap: 40px;
  }

  .info-field {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .field-container {
    background-color: #f1f5f9;
    padding: 12px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
  }

  .field-label {
    font-size: 0.875rem;
    color: #6b7280;
    margin-bottom: 4px;
  }

  .field-value {
    font-size: 1rem;
    color: #111827;
    font-weight: 500;
  }

  .charity-container {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
  }

  .charity-logo {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
  }

  .charity-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .charity-info {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .charity-name {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .charity-reg {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }

  .charity-details-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .charity-detail-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .detail-label {
    font-size: 0.9rem;
    color: #6b7280;
  }

  .detail-value-container {
    background-color: #f1f5f9;
    padding: 16px;
    border-radius: 8px;
  }

  .detail-value-title {
    font-size: 1rem;
    font-weight: 500;
    color: #111827;
    display: block;
    margin-bottom: 8px;
  }

  .detail-value {
    font-size: 1rem;
    font-weight: 500;
    color: #111827;
  }

  .charity-description {
    font-size: 0.875rem;
    color: #4b5563;
    line-height: 1.5;
    margin: 0;
  }

  .employee-link {
    display: inline-flex;
    align-items: center;
    color: #2563eb;
    text-decoration: none;
    transition: color 0.2s;
  }

  .employee-link:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }

  .employee-link .field-value {
    color: inherit;
  }

  .charity-name-link {
    text-decoration: none;
    color: inherit;
    display: inline-block;
  }

  .charity-name-link:hover .charity-name {
    color: #2563eb;
  }

  .charity-link {
    font-size: 0.875rem;
    color: #2563eb;
    text-decoration: none;
    margin-top: 16px;
    display: inline-flex;
    align-items: center;
    transition: color 0.2s;
  }

  .charity-link:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }

  .note-container {
    border-radius: 8px;
    padding: 16px;
  }

  .note-container-rejected {
    background-color: #FEE2E2;
    border-left: 4px solid #DC2626;
  }

  .note-container-approved {
    background-color: #D1FAE5;
    border-left: 4px solid #059669;
  }

  .employee-note {
    font-size: 0.875rem;
    color: #111827;
    line-height: 1.6;
    margin: 0;
    font-weight: 500;
  }

  .action-buttons-group {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    margin-top: 8px;
  }

  .utility-buttons {
    display: flex;
    gap: 0.75rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
  }

  .utility-buttons svg {
    flex-shrink: 0;
  }

  .action-buttons :global(button) {
    flex: 1;
    height: 52px;
    border-radius: 100px;
    padding: 10px;
    gap: 10px;
  }

  .action-info {
    margin-bottom: 20px;
  }

  .action-description {
    font-size: 0.875rem;
    color: #111827;
    font-weight: 500;
    margin: 0 0 8px 0;
  }

  .action-detail {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
  }

  .amount-value {
    color: #059669;
    font-weight: 600;
  }

  .total-container {
    background-color: #D1FAE5;
    border-left: 4px solid #059669;
  }

  .total-amount {
    color: #059669;
    font-weight: 700;
    font-size: 1.125rem;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .info-row {
      flex-direction: column;
      gap: 16px;
    }

    .charity-container {
      flex-direction: column;
      align-items: flex-start;
    }

    .action-buttons {
      flex-direction: column;
      gap: 12px;
    }
  }
</style> 